import {
  Activity,
  ArrowLeftRight,
  Bone,
  Check,
  ChevronRight,
  ClipboardCheck,
  Download,
  Eye,
  FileJson,
  FileSearch,
  FileText,
  Gauge,
  Info,
  LoaderCircle,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Printer,
  RefreshCcw,
  ShieldCheck,
  Sparkles,
  Upload,
  UserRound,
  X,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { PatientReport } from "./components/PatientReport.jsx";
import { ComparisonReport } from "./components/ComparisonReport.jsx";
import {
  Field,
  Metric,
  SectionHeading,
  SelectField,
  StatusNote,
  TextAreaField,
} from "./components/ui.jsx";
import { deriveDexaRecord, toFiniteNumber } from "./lib/dexaCalculations.js";
import { buildDexaInterpretation } from "./lib/dexaInterpretation.js";
import { buildDexaComparison, sortDexaRecords } from "./lib/dexaComparison.js";
import { parseDexaPages } from "./lib/dexaParser.js";
import { createAnonymizedDemoRecord } from "./lib/dexaRecord.js";
import { extractDexaPdf } from "./lib/pdfExtraction.js";
import {
  exportRecordJson,
  exportReportHtml,
  exportReportPdf,
} from "./lib/reportExport.js";

const workflowSteps = [
  ["import", "Importar", "Cargar el PDF del equipo", Upload],
  ["review", "Revisar datos", "Confirmar y completar variables", ClipboardCheck],
  ["interpret", "Interpretar", "Lectura clínica asistida", FileSearch],
  ["report", "Informe", "Entregable para paciente", FileText],
];

const regionLabels = {
  leftArm: "Brazo izquierdo",
  rightArm: "Brazo derecho",
  trunk: "Tronco",
  leftLeg: "Pierna izquierda",
  rightLeg: "Pierna derecha",
  head: "Cabeza",
};

const boneRegionLabels = {
  leftArm: "Brazo izquierdo",
  rightArm: "Brazo derecho",
  leftRibs: "Costillas izquierdas",
  rightRibs: "Costillas derechas",
  thoracicSpine: "Columna dorsal",
  lumbarSpine: "Columna lumbar regional",
  pelvis: "Pelvis",
  leftLeg: "Pierna izquierda",
  rightLeg: "Pierna derecha",
  head: "Cabeza",
};

function format(value, digits = 1, suffix = "") {
  const numeric = toFiniteNumber(value);
  return numeric !== null
    ? `${numeric.toLocaleString("es-CO", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })}${suffix}`
    : "Pendiente";
}

function normalizedIdentity(value) {
  return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\W+/g, "").toLowerCase();
}

function validateSamePatient(records) {
  const ids = records.map((item) => normalizedIdentity(item.patient.patientId)).filter(Boolean);
  if (ids.length >= 2) return new Set(ids).size === 1;
  const names = records.map((item) => normalizedIdentity(item.patient.name)).filter(Boolean);
  return names.length < 2 || new Set(names).size === 1;
}

function comparisonRecordKey(record) {
  return [
    normalizedIdentity(record.patient.patientId || record.patient.name),
    record.exam.date || "sin-fecha",
    normalizedIdentity(record.exam.sourceFileName),
  ].join("|");
}

function updateNestedValue(source, path, value) {
  const clone = structuredClone(source);
  const keys = path.split(".");
  let target = clone;
  for (const key of keys.slice(0, -1)) target = target[key];
  target[keys.at(-1)] = value;
  return clone;
}

function recordCompleteness(record) {
  if (!record) return { percent: 0, complete: 0, total: 8 };
  const checks = [
    record.patient.name,
    record.patient.sex,
    record.patient.heightCm,
    record.exam.date,
    record.composition.total.totalMassG,
    record.composition.total.fatMassG,
    record.bone.hip.total.bmd || record.bone.hip.neck.bmd,
    record.bone.lumbar.total.bmd,
  ];
  const complete = checks.filter((item) => item !== null && item !== undefined && item !== "").length;
  return { percent: Math.round(complete / checks.length * 100), complete, total: checks.length };
}

function AppHeader({ record, sidebarCollapsed, setSidebarCollapsed, onReset }) {
  return (
    <header className="app-header">
      <div className="app-header__brand">
        <button
          type="button"
          className="icon-button app-header__menu"
          onClick={() => setSidebarCollapsed((value) => !value)}
          aria-label={sidebarCollapsed ? "Abrir navegación" : "Contraer navegación"}
          title={sidebarCollapsed ? "Abrir navegación" : "Contraer navegación"}
        >
          {sidebarCollapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </button>
        <span className="app-logo"><Activity /></span>
        <div><strong>Informe DXA</strong><small>Interpretación profesional</small></div>
      </div>
      <div className="app-header__status">
        <ShieldCheck />
        <span><strong>Procesamiento privado</strong><small>El PDF permanece en este dispositivo</small></span>
      </div>
      {record ? (
        <button type="button" className="button button--quiet" onClick={onReset}>
          <RefreshCcw /> Nuevo informe
        </button>
      ) : null}
    </header>
  );
}

function WorkflowSidebar({
  activeStep,
  setActiveStep,
  record,
  collapsed,
  setCollapsed,
}) {
  const completeness = recordCompleteness(record);
  return (
    <aside className={`workflow-sidebar ${collapsed ? "workflow-sidebar--collapsed" : ""}`}>
      <button
        type="button"
        className="icon-button workflow-sidebar__mobile-close"
        onClick={() => setCollapsed(true)}
        aria-label="Cerrar navegación"
      >
        <X />
      </button>
      <div className="workflow-sidebar__intro">
        <span className="eyebrow">Flujo de trabajo</span>
        <strong>{record?.patient.name || "Nuevo informe"}</strong>
        <small>{record ? `${completeness.percent}% de datos esenciales` : "Empieza con el PDF del equipo"}</small>
        {record ? <div className="progress-bar"><span style={{ width: `${completeness.percent}%` }} /></div> : null}
      </div>
      <nav aria-label="Etapas del informe DXA">
        {workflowSteps.map(([id, label, description, Icon], index) => (
          <button
            key={id}
            type="button"
            className={activeStep === id ? "active" : ""}
            onClick={() => {
              setActiveStep(id);
              if (window.matchMedia("(max-width: 760px)").matches) setCollapsed(true);
            }}
            disabled={!record && id !== "import"}
            aria-current={activeStep === id ? "step" : undefined}
          >
            <span><Icon /></span>
            <span><strong>{index + 1}. {label}</strong><small>{description}</small></span>
            <ChevronRight />
          </button>
        ))}
      </nav>
      <div className="workflow-sidebar__notice">
        <Info />
        <p>La herramienta organiza y explica resultados. La profesional conserva la decisión final.</p>
      </div>
    </aside>
  );
}

function ImportStep({
  record,
  comparisonRecords,
  analysisMode,
  onModeChange,
  analysisProfile,
  onProfileChange,
  onFile,
  onDemo,
  busy,
  progress,
  error,
  onContinueComparison,
  onRemoveComparison,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const profileSelected = Boolean(analysisProfile);
  const handleFiles = (files) => {
    if (!profileSelected) return;
    const selected = Array.from(files || []);
    if (selected.length) onFile(selected);
  };
  return (
    <section className="workspace-step">
      <SectionHeading
        eyebrow="Etapa 1"
        title="Importar informe del equipo"
        description="Carga el PDF generado por el densitómetro. La lectura y las imágenes se procesan en el navegador."
      />
      <section className="analysis-mode" aria-labelledby="analysis-mode-title">
        <div className="analysis-profile__heading">
          <span className="eyebrow">Tipo de informe</span>
          <h2 id="analysis-mode-title">¿Qué deseas hacer?</h2>
          <p>El análisis individual usa un PDF. El comparativo contrasta dos o más valoraciones del mismo paciente.</p>
        </div>
        <div className="analysis-profile__options" role="group" aria-label="Tipo de análisis DXA">
          <button type="button" className={`analysis-profile__option ${analysisMode === "single" ? "is-selected" : ""}`} aria-pressed={analysisMode === "single"} onClick={() => onModeChange("single")} disabled={busy}>
            <FileSearch aria-hidden="true" /><span><strong>Analizar un informe</strong><small>Interpretación completa de una valoración.</small></span><Check aria-hidden="true" />
          </button>
          <button type="button" className={`analysis-profile__option ${analysisMode === "comparison" ? "is-selected" : ""}`} aria-pressed={analysisMode === "comparison"} onClick={() => onModeChange("comparison")} disabled={busy}>
            <ArrowLeftRight aria-hidden="true" /><span><strong>Análisis comparativo</strong><small>Compara cambios entre dos o más fechas.</small></span><Check aria-hidden="true" />
          </button>
        </div>
      </section>
      <section className="analysis-profile" aria-labelledby="analysis-profile-title">
        <div className="analysis-profile__heading">
          <span className="eyebrow">Antes de cargar el informe</span>
          <h2 id="analysis-profile-title">¿Qué perfil se analizará?</h2>
          <p>Esta elección define el rango de referencia usado para interpretar grasa corporal y masa magra.</p>
        </div>
        <div className="analysis-profile__options" role="group" aria-label="Perfil de análisis DXA">
          <button
            type="button"
            className={`analysis-profile__option ${analysisProfile === "athlete" ? "is-selected" : ""}`}
            aria-pressed={analysisProfile === "athlete"}
            onClick={() => onProfileChange("athlete")}
            disabled={busy}
          >
            <Activity aria-hidden="true" />
            <span><strong>Deportista</strong><small>Usa referencias de deporte y fitness según edad y sexo.</small></span>
            <Check aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`analysis-profile__option ${analysisProfile === "general" ? "is-selected" : ""}`}
            aria-pressed={analysisProfile === "general"}
            onClick={() => onProfileChange("general")}
            disabled={busy}
          >
            <UserRound aria-hidden="true" />
            <span><strong>Salud general</strong><small>Usa referencias generales según edad y sexo.</small></span>
            <Check aria-hidden="true" />
          </button>
        </div>
      </section>
      <div
        className={`upload-zone ${dragging ? "upload-zone--dragging" : ""} ${busy ? "upload-zone--busy" : ""} ${!profileSelected ? "upload-zone--locked" : ""}`}
        aria-disabled={!profileSelected}
        onDragOver={(event) => {
          event.preventDefault();
          if (!profileSelected) return;
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          handleFiles(event.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple={analysisMode === "comparison"}
          disabled={!profileSelected || busy}
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = "";
          }}
        />
        {busy ? <LoaderCircle className="spin" /> : <Upload />}
        <h2>{busy ? "Leyendo informe DXA" : profileSelected ? analysisMode === "comparison" ? comparisonRecords.length ? "Añade otra valoración del mismo paciente" : "Adjunta la primera valoración" : "Arrastra aquí el PDF del equipo" : "Selecciona primero el perfil"}</h2>
        <p>{busy
          ? `${progress.fileCount > 1 ? `Informe ${progress.fileNumber} de ${progress.fileCount} · ` : ""}Procesando página ${progress.pageNumber || 0} de ${progress.pageCount || "…"}`
          : profileSelected
            ? analysisMode === "comparison" ? `${comparisonRecords.length ? `${comparisonRecords.length} valoración${comparisonRecords.length === 1 ? "" : "es"} cargada${comparisonRecords.length === 1 ? "" : "s"}` : "Ninguna valoración cargada"}. Puedes buscarlas en carpetas diferentes y añadirlas una por una.` : "También puedes elegirlo desde el equipo. Tamaño máximo: 35 MB."
            : "La carga se habilitará cuando elijas una de las dos opciones anteriores."}</p>
        {busy ? (
          <div className="upload-progress">
            <span style={{
              width: `${progress.pageCount ? progress.pageNumber / progress.pageCount * 100 : 8}%`,
            }}
            />
          </div>
        ) : (
          <button type="button" className="button button--primary" onClick={() => inputRef.current?.click()} disabled={!profileSelected}>
            <Upload /> {analysisMode === "comparison" ? comparisonRecords.length ? "Añadir otro PDF" : "Elegir primer PDF" : "Elegir PDF"}
          </button>
        )}
      </div>
      {analysisMode === "comparison" && comparisonRecords.length ? (
        <section className="comparison-upload-summary" aria-label="Valoraciones añadidas">
          <header>
            <div><span className="eyebrow">Informes para comparar</span><h3>{comparisonRecords.length} valoración{comparisonRecords.length === 1 ? "" : "es"} añadida{comparisonRecords.length === 1 ? "" : "s"}</h3></div>
            <span className={comparisonRecords.length >= 2 ? "is-ready" : ""}>{comparisonRecords.length >= 2 ? "Listo para comparar" : "Falta al menos un PDF"}</span>
          </header>
          <div className="comparison-upload-summary__list">
            {comparisonRecords.map((item, index) => (
              <div key={comparisonRecordKey(item)}>
                <span>{index + 1}</span>
                <div><strong>{item.exam.date || "Fecha pendiente"}</strong><small>{item.exam.sourceFileName || item.patient.name || "Informe DXA"}</small></div>
                <button type="button" onClick={() => onRemoveComparison(index)} aria-label={`Quitar ${item.exam.sourceFileName || `valoración ${index + 1}`}`} disabled={busy}><X /></button>
              </div>
            ))}
          </div>
          <div className="comparison-upload-summary__actions">
            <button type="button" className="button" onClick={() => inputRef.current?.click()} disabled={busy}><Upload /> Añadir otro PDF</button>
            <button type="button" className="button button--primary" onClick={onContinueComparison} disabled={busy || comparisonRecords.length < 2}><ArrowLeftRight /> Continuar con comparación</button>
          </div>
        </section>
      ) : null}
      {error ? <StatusNote status="review" title="No fue posible leer el archivo">{error}</StatusNote> : null}
      <div className="privacy-band">
        <ShieldCheck />
        <div>
          <strong>Privacidad por diseño</strong>
          <p>Los PDF, las imágenes y los identificadores se procesan en este dispositivo. En modo comparativo, las valoraciones se conservan juntas únicamente durante esta sesión.</p>
        </div>
      </div>
      <div className="import-help">
        <div>
          <span className="eyebrow">Compatibilidad actual</span>
          <h3>Informes Hologic Horizon en español</h3>
          <p>Se reconocen composición corporal, regiones, índices adiposos, masa magra, cuerpo entero, cadera y columna lumbar. Los valores que no se detecten quedan disponibles para ingreso manual.</p>
        </div>
        <button type="button" className="button" onClick={onDemo} disabled={busy || !profileSelected}>
          <Sparkles /> Abrir ejemplo anonimizado
        </button>
      </div>
      {record ? (
        <StatusNote status="favorable" title="Informe listo para revisar">
          Se detectaron {record.source.detectedFields} valores en {record.source.pageCount} páginas.
        </StatusNote>
      ) : null}
    </section>
  );
}

function PatientReview({ record, update }) {
  return (
    <section className="editor-section">
      <header><UserRound /><div><h3>Paciente y examen</h3><p>Confirma identificación, referencia poblacional y contexto.</p></div></header>
      <div className="form-grid form-grid--four">
        <Field label="Nombre completo" value={record.patient.name} onChange={(value) => update("patient.name", value)} />
        <Field label="Identificador" value={record.patient.patientId} onChange={(value) => update("patient.patientId", value)} hint="Puedes borrarlo del entregable si no es necesario." />
        <Field label="Fecha de nacimiento" type="date" value={record.patient.birthDate} onChange={(value) => update("patient.birthDate", value)} />
        <Field label="Edad calculada" type="number" value={record.patient.age} onChange={(value) => update("patient.age", value)} />
        <SelectField
          label="Sexo de referencia"
          value={record.patient.sex}
          onChange={(value) => update("patient.sex", value)}
          options={[["", "Seleccionar"], ["female", "Mujer"], ["male", "Hombre"]]}
        />
        <Field label="Población de referencia" value={record.patient.ethnicity} onChange={(value) => update("patient.ethnicity", value)} />
        <Field label="Estatura cm" type="number" step="0.1" value={record.patient.heightCm} onChange={(value) => update("patient.heightCm", value)} />
        <Field label="Peso de báscula kg" type="number" step="0.1" value={record.patient.scaleWeightKg} onChange={(value) => update("patient.scaleWeightKg", value)} />
        <Field label="Fecha del examen" type="date" value={record.exam.date} onChange={(value) => update("exam.date", value)} />
        <Field label="ID del examen" value={record.exam.examId} onChange={(value) => update("exam.examId", value)} />
        <Field label="Fabricante" value={record.exam.vendor} onChange={(value) => update("exam.vendor", value)} />
        <Field label="Modelo" value={record.exam.model} onChange={(value) => update("exam.model", value)} />
        <Field label="Deporte o actividad" value={record.exam.sport} onChange={(value) => update("exam.sport", value)} />
        <Field label="Profesional remitente" value={record.exam.referrer} onChange={(value) => update("exam.referrer", value)} />
      </div>
    </section>
  );
}

function CompositionReview({ record, update }) {
  const total = record.composition.total;
  return (
    <>
      <section className="editor-section">
        <header><Gauge /><div><h3>Composición corporal total</h3><p>Las masas se conservan en gramos para coincidir con el informe técnico.</p></div></header>
        <div className="form-grid form-grid--six">
          <Field label="Masa total g" type="number" step="0.1" value={total.totalMassG} onChange={(value) => update("composition.total.totalMassG", value)} />
          <Field label="Masa grasa g" type="number" step="0.1" value={total.fatMassG} onChange={(value) => update("composition.total.fatMassG", value)} />
          <Field label="Masa magra g" type="number" step="0.1" value={total.leanMassG} onChange={(value) => update("composition.total.leanMassG", value)} />
          <Field label="Magro + CMO g" type="number" step="0.1" value={total.leanPlusBmcG} onChange={(value) => update("composition.total.leanPlusBmcG", value)} />
          <Field label="CMO g" type="number" step="0.1" value={total.bmcG} onChange={(value) => update("composition.total.bmcG", value)} />
          <Field label="Grasa %" type="number" step="0.1" value={total.fatPercent} onChange={(value) => update("composition.total.fatPercent", value)} />
        </div>
        <div className="derived-row">
          <span>Masa DXA: <strong>{format(total.totalMassKg, 2, " kg")}</strong></span>
          <span>Grasa: <strong>{format(total.fatMassKg, 2, " kg")}</strong></span>
          <span>Magra: <strong>{format(total.leanMassKg, 2, " kg")}</strong></span>
          <span>CMO: <strong>{format(total.bmcKg, 2, " kg")}</strong></span>
        </div>
      </section>
      <section className="editor-section">
        <header><Activity /><div><h3>Composición regional</h3><p>Verifica cada región antes de interpretar simetrías.</p></div></header>
        <div className="compact-table-wrap">
          <table className="compact-table">
            <thead><tr><th>Región</th><th>Grasa g</th><th>Magra g</th><th>CMO g</th><th>Total g</th><th>Grasa %</th></tr></thead>
            <tbody>
              {Object.entries(regionLabels).map(([key, label]) => {
                const region = record.composition.regions[key];
                return (
                  <tr key={key}>
                    <th>{label}</th>
                    {[
                      ["fatMassG", "Grasa"],
                      ["leanMassG", "Magra"],
                      ["bmcG", "CMO"],
                      ["totalMassG", "Total"],
                      ["fatPercent", "Grasa %"],
                    ].map(([field, aria]) => (
                      <td key={field}><input aria-label={`${label} ${aria}`} type="number" step="0.1" value={region[field] ?? ""} onChange={(event) => update(`composition.regions.${key}.${field}`, event.target.value === "" ? null : Number(event.target.value))} /></td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function IndicesReview({ record, update }) {
  const indices = record.composition.indices;
  return (
    <section className="editor-section">
      <header><Activity /><div><h3>Índices adiposos y musculares</h3><p>Los valores derivados se completan automáticamente cuando existen masas y estatura.</p></div></header>
      <div className="form-grid form-grid--four">
        <Field label="IMC kg/m²" type="number" step="0.1" value={indices.bmi} onChange={(value) => update("composition.indices.bmi", value)} />
        <Field label="IMG kg/m²" type="number" step="0.01" value={indices.fmi} onChange={(value) => update("composition.indices.fmi", value)} />
        <Field label="IMM kg/m²" type="number" step="0.01" value={indices.lmi} onChange={(value) => update("composition.indices.lmi", value)} />
        <Field label="IMMA kg/m²" type="number" step="0.01" value={indices.almi} onChange={(value) => update("composition.indices.almi", value)} />
        <Field label="Androide / ginoide" type="number" step="0.01" value={indices.androidGynoidRatio} onChange={(value) => update("composition.indices.androidGynoidRatio", value)} />
        <Field label="Tronco / piernas" type="number" step="0.01" value={indices.trunkLegFatRatio} onChange={(value) => update("composition.indices.trunkLegFatRatio", value)} />
        <Field label="Tronco / extremidades" type="number" step="0.01" value={indices.trunkExtremityFatRatio} onChange={(value) => update("composition.indices.trunkExtremityFatRatio", value)} />
        <Field label="VAT área cm²" type="number" step="0.1" value={indices.vatAreaCm2} onChange={(value) => update("composition.indices.vatAreaCm2", value)} />
        <Field label="VAT masa g" type="number" step="1" value={indices.vatMassG} onChange={(value) => update("composition.indices.vatMassG", value)} />
        <Field label="VAT volumen cm³" type="number" step="1" value={indices.vatVolumeCm3} onChange={(value) => update("composition.indices.vatVolumeCm3", value)} />
        <Field label="Percentil grasa EC" type="number" min="0" max="100" value={indices.bodyFatPercentileAgeMatched} onChange={(value) => update("composition.indices.bodyFatPercentileAgeMatched", value)} />
        <Field label="Percentil IMG GN" type="number" min="0" max="100" value={indices.fmiPercentileYoung} onChange={(value) => update("composition.indices.fmiPercentileYoung", value)} />
        <Field label="Percentil IMG EC" type="number" min="0" max="100" value={indices.fmiPercentileAgeMatched} onChange={(value) => update("composition.indices.fmiPercentileAgeMatched", value)} />
        <Field label="Percentil tronco/piernas GN" type="number" min="0" max="100" value={indices.trunkLegFatPercentileYoung} onChange={(value) => update("composition.indices.trunkLegFatPercentileYoung", value)} />
        <Field label="Percentil tronco/piernas EC" type="number" min="0" max="100" value={indices.trunkLegFatPercentileAgeMatched} onChange={(value) => update("composition.indices.trunkLegFatPercentileAgeMatched", value)} />
        <Field label="Percentil tronco/extremidades GN" type="number" min="0" max="100" value={indices.trunkExtremityFatPercentileYoung} onChange={(value) => update("composition.indices.trunkExtremityFatPercentileYoung", value)} />
        <Field label="Percentil tronco/extremidades EC" type="number" min="0" max="100" value={indices.trunkExtremityFatPercentileAgeMatched} onChange={(value) => update("composition.indices.trunkExtremityFatPercentileAgeMatched", value)} />
        <Field label="Percentil IMM EC" type="number" min="0" max="100" value={indices.lmiPercentileAgeMatched} onChange={(value) => update("composition.indices.lmiPercentileAgeMatched", value)} />
        <Field label="Percentil IMMA EC" type="number" min="0" max="100" value={indices.almiPercentileAgeMatched} onChange={(value) => update("composition.indices.almiPercentileAgeMatched", value)} />
      </div>
    </section>
  );
}

function BmdRow({ label, path, value, update }) {
  return (
    <tr>
      <th>{label}</th>
      {[
        ["areaCm2", "Área"],
        ["bmcG", "CMO"],
        ["bmd", "DMO"],
        ["tScore", "T-score"],
        ["zScore", "Z-score"],
      ].map(([field, aria]) => (
        <td key={field}>
          <input
            aria-label={`${label} ${aria}`}
            type="number"
            step="0.001"
            value={value?.[field] ?? ""}
            onChange={(event) => update(`${path}.${field}`, event.target.value === "" ? null : Number(event.target.value))}
          />
        </td>
      ))}
    </tr>
  );
}

function BoneReview({ record, update }) {
  const hipSide = record.bone.hip.side === "right" ? "derecha" : record.bone.hip.side === "left" ? "izquierda" : "pendiente";
  return (
    <>
      <section className="editor-section">
        <header><Bone /><div><h3>Densidad ósea central</h3><p>Cadera y columna determinan la lectura densitométrica principal.</p></div></header>
        <div className="compact-table-wrap">
          <table className="compact-table">
            <thead><tr><th>Región</th><th>Área cm²</th><th>CMO g</th><th>DMO g/cm²</th><th>T-score</th><th>Z-score</th></tr></thead>
            <tbody>
              <BmdRow label="Cuerpo entero" path="bone.wholeBody" value={record.bone.wholeBody} update={update} />
              <BmdRow label={`Cuello femoral · cadera ${hipSide}`} path="bone.hip.neck" value={record.bone.hip.neck} update={update} />
              <BmdRow label={`Cadera total · ${hipSide}`} path="bone.hip.total" value={record.bone.hip.total} update={update} />
              {["l1", "l2", "l3", "l4", "total"].map((key) => (
                <BmdRow key={key} label={key === "total" ? "L1-L4" : key.toUpperCase()} path={`bone.lumbar.${key}`} value={record.bone.lumbar[key]} update={update} />
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="editor-section">
        <header><Bone /><div><h3>DMO de cuerpo entero por región</h3><p>Información complementaria preservada del informe técnico.</p></div></header>
        <div className="form-grid form-grid--five">
          {Object.entries(boneRegionLabels).map(([key, label]) => (
            <Field key={key} label={label} type="number" step="0.001" value={record.bone.wholeBodyRegions[key].bmd} onChange={(value) => update(`bone.wholeBodyRegions.${key}.bmd`, value)} />
          ))}
        </div>
      </section>
    </>
  );
}

function ProfessionalReview({ record, update }) {
  return (
    <section className="editor-section">
      <header><ShieldCheck /><div><h3>Criterio profesional</h3><p>Configura referencias y completa el cierre antes de exportar.</p></div></header>
      <div className="form-grid form-grid--four">
        <SelectField
          label="Perfil de grasa corporal"
          value={record.professional.populationProfile}
          onChange={(value) => update("professional.populationProfile", value)}
          options={[["athlete", "Deporte y fitness"], ["general", "Salud general"]]}
        />
        <SelectField
          label="Estado menopáusico"
          value={record.professional.menopausalStatus}
          onChange={(value) => update("professional.menopausalStatus", value)}
          options={[
            ["not_applicable", "No aplica / no confirmado"],
            ["premenopausal", "Premenopáusica"],
            ["postmenopausal", "Posmenopáusica"],
          ]}
          hint="Define si la lectura ósea principal usa Z-score o T-score."
        />
        <Field label="DMO regional mínima" type="number" step="0.01" value={record.professional.regionalBmdReferenceMin} onChange={(value) => update("professional.regionalBmdReferenceMin", value)} hint="Límite operativo del formato en g/cm²." />
        <Field label="DMO regional máxima" type="number" step="0.01" value={record.professional.regionalBmdReferenceMax} onChange={(value) => update("professional.regionalBmdReferenceMax", value)} />
        <SelectField
          label="Regla regional del Excel"
          value={record.professional.useWorkbookRegionalBmdRules ? "yes" : "no"}
          onChange={(value) => update("professional.useWorkbookRegionalBmdRules", value === "yes")}
          options={[["yes", "Mostrar como referencia"], ["no", "Solo mostrar valores"]]}
        />
      </div>
      <div className="form-grid form-grid--two">
        <TextAreaField label="Contexto clínico" value={record.professional.clinicalContext} onChange={(value) => update("professional.clinicalContext", value)} placeholder="Antecedentes, motivo, síntomas, lesiones, etapa deportiva…" />
        <TextAreaField label="Conclusión profesional personalizada" value={record.professional.conclusionOverride} onChange={(value) => update("professional.conclusionOverride", value)} placeholder="Si queda vacío, se usa la conclusión asistida y trazable." />
        <TextAreaField label="Recomendaciones" value={record.professional.recommendations} onChange={(value) => update("professional.recommendations", value)} placeholder="Seguimiento, entrenamiento, nutrición, remisión o control…" />
        <div className="form-grid form-grid--two form-grid--nested">
          <Field label="Profesional que aprueba" value={record.professional.approvedBy} onChange={(value) => update("professional.approvedBy", value)} />
          <Field label="Entidad" value={record.professional.organization} onChange={(value) => update("professional.organization", value)} />
          <Field label="Registro profesional" value={record.professional.license} onChange={(value) => update("professional.license", value)} />
        </div>
      </div>
    </section>
  );
}

function ReviewStep({ record, update, onContinue }) {
  return (
    <section className="workspace-step">
      <SectionHeading
        eyebrow="Etapa 2"
        title="Revisar datos extraídos"
        description="Los valores calculados se actualizan al editar. Ningún texto interpretativo reemplaza la aprobación profesional."
        actions={<button type="button" className="button button--primary" onClick={onContinue}>Interpretar <ChevronRight /></button>}
      />
      <div className="source-summary">
        <div><FileText /><span><strong>{record.exam.sourceFileName || "Carga manual"}</strong><small>{record.source.pageCount} páginas · parser {record.source.parser}</small></span></div>
        <div className="source-summary__confidence">
          <span>Confianza de extracción</span>
          <strong>{Math.round(record.source.confidence * 100)}%</strong>
        </div>
      </div>
      {record.source.warnings?.length ? (
        <details className="warning-disclosure">
          <summary><Info /> {record.source.warnings.length} observación(es) de extracción</summary>
          <ul>{record.source.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
        </details>
      ) : null}
      <PatientReview record={record} update={update} />
      <CompositionReview record={record} update={update} />
      <IndicesReview record={record} update={update} />
      <BoneReview record={record} update={update} />
      <ProfessionalReview record={record} update={update} />
    </section>
  );
}

function InterpretStep({ interpretation, onContinue }) {
  const { record } = interpretation;
  return (
    <section className="workspace-step">
      <SectionHeading
        eyebrow="Etapa 3"
        title="Interpretación asistida"
        description="Cada conclusión muestra el dato y el criterio utilizado para que pueda revisarse."
        actions={<button type="button" className="button button--primary" onClick={onContinue}>Ver informe <ChevronRight /></button>}
      />
      <div className="interpret-headline">
        {Object.values(interpretation.headline).map((item) => <Metric key={item.label} {...item} />)}
      </div>
      <section className="interpret-block">
        <header><ScaleIcon /><div><h3>Composición corporal</h3><p>{interpretation.bodyComposition.scanSummary}</p></div></header>
        <StatusNote status={interpretation.headline.fat.status} title={interpretation.bodyComposition.classification.label}>
          {interpretation.bodyComposition.referenceRange
            ? `Comparación con rango ${interpretation.bodyComposition.referenceRange[0]}–${interpretation.bodyComposition.referenceRange[1]}% para el perfil seleccionado.`
            : "Selecciona sexo y perfil poblacional para aplicar un rango."}
        </StatusNote>
      </section>
      <div className="interpret-grid">
        <section className="interpret-block">
          <header><Activity /><div><h3>Simetría</h3><p>Comparación bilateral de masa total.</p></div></header>
          <StatusNote status={interpretation.symmetry.arms.status} title={interpretation.symmetry.arms.title}>{interpretation.symmetry.arms.text}</StatusNote>
          <StatusNote status={interpretation.symmetry.legs.status} title={interpretation.symmetry.legs.title}>{interpretation.symmetry.legs.text}</StatusNote>
        </section>
        <section className="interpret-block">
          <header><Activity /><div><h3>Grasa visceral</h3><p>{format(record.composition.indices.vatAreaCm2, 1, " cm²")} de área estimada.</p></div></header>
          <StatusNote status={interpretation.adiposity.vat.status} title="Lectura con punto operativo configurable">{interpretation.adiposity.vat.note}</StatusNote>
        </section>
        <section className="interpret-block">
          <header><Gauge /><div><h3>Masa magra</h3><p>IMMA {format(interpretation.muscle.almi, 2, " kg/m²")}.</p></div></header>
          <StatusNote status={interpretation.muscle.status} title="Índice de masa magra apendicular">{interpretation.muscle.note}</StatusNote>
        </section>
        <section className="interpret-block">
          <header><Bone /><div><h3>Densidad ósea</h3><p>Lectura principal con {interpretation.bone.mode === "t" ? "T-score" : "Z-score"}.</p></div></header>
          <StatusNote status={interpretation.bone.status} title={interpretation.bone.title}>{interpretation.bone.text}</StatusNote>
        </section>
      </div>
      <section className="interpret-block interpret-block--conclusion">
        <header><FileText /><div><h3>Conclusión integrada</h3><p>{interpretation.conclusion}</p></div></header>
      </section>
      <div className="source-links">
        <strong>Fuentes utilizadas</strong>
        {interpretation.sources.map((source) => <a key={source.id} href={source.url} target="_blank" rel="noreferrer">{source.label}</a>)}
      </div>
    </section>
  );
}

function ScaleIcon() {
  return <Gauge />;
}

function ReportStep({
  interpretation,
  comparison,
  includeOriginalPages,
  setIncludeOriginalPages,
}) {
  const record = comparison?.currentRecord || interpretation.record;
  const [pdfProgress, setPdfProgress] = useState(null);
  const [pdfError, setPdfError] = useState("");

  const handlePdfExport = async () => {
    setPdfError("");
    setPdfProgress({ current: 0, total: 0 });
    try {
      await exportReportPdf(record, setPdfProgress);
    } catch (error) {
      setPdfError(error instanceof Error ? error.message : "No fue posible generar el PDF.");
    } finally {
      setPdfProgress(null);
    }
  };

  return (
    <section className="workspace-step workspace-step--report">
      <SectionHeading
        eyebrow="Etapa 4"
        title="Informe para el paciente"
        description="Revisa la vista final. El PDF conserva estas mismas páginas, imágenes, colores y proporciones."
        actions={(
          <>
            <button type="button" className="button" onClick={() => exportRecordJson(comparison ? { ...record, comparisonRecords: comparison.records } : record)}><FileJson /> JSON</button>
            <button type="button" className="button" onClick={() => exportReportHtml(record)}><Download /> HTML</button>
            <button type="button" className="button button--primary" onClick={handlePdfExport} disabled={Boolean(pdfProgress)}>
              {pdfProgress ? <LoaderCircle className="spin" /> : <Printer />}
              {pdfProgress?.total ? `Generando ${pdfProgress.current}/${pdfProgress.total}` : pdfProgress ? "Preparando PDF" : "Exportar PDF"}
            </button>
          </>
        )}
      />
      {pdfError ? <StatusNote tone="danger">{pdfError}</StatusNote> : null}
      <div className="report-toolbar">
        <label className="switch-control">
          <input type="checkbox" checked={includeOriginalPages} onChange={(event) => setIncludeOriginalPages(event.target.checked)} />
          <span />
          Incluir páginas originales como anexo
        </label>
        <span><Eye /> Vista interpretada con imágenes del examen</span>
      </div>
      <div className="report-canvas">
        {comparison
          ? <ComparisonReport comparison={comparison} includeOriginalPages={includeOriginalPages} />
          : <PatientReport interpretation={interpretation} includeOriginalPages={includeOriginalPages} />}
      </div>
    </section>
  );
}

function MobileWorkflowButton({ collapsed, setCollapsed }) {
  if (!collapsed) return null;
  return (
    <button type="button" className="mobile-workflow-button" onClick={() => setCollapsed(false)}>
      <Menu /> Etapas
    </button>
  );
}

export function App() {
  const [record, setRecord] = useState(null);
  const [comparisonRecords, setComparisonRecords] = useState([]);
  const [selectedRecordIndex, setSelectedRecordIndex] = useState(0);
  const [analysisMode, setAnalysisMode] = useState("single");
  const [analysisProfile, setAnalysisProfile] = useState("");
  const [activeStep, setActiveStep] = useState("import");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => window.matchMedia("(max-width: 760px)").matches,
  );
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ pageNumber: 0, pageCount: 0 });
  const [error, setError] = useState("");
  const [includeOriginalPages, setIncludeOriginalPages] = useState(false);
  const interpretation = useMemo(
    () => record ? buildDexaInterpretation(record) : null,
    [record],
  );
  const comparison = useMemo(
    () => analysisMode === "comparison" ? buildDexaComparison(comparisonRecords) : null,
    [analysisMode, comparisonRecords],
  );

  const update = (path, value) => {
    if (path === "professional.populationProfile") setAnalysisProfile(value);
    setRecord((current) => {
      const updated = deriveDexaRecord(updateNestedValue(current, path, value));
      if (analysisMode === "comparison") {
        setComparisonRecords((items) => items.map((item, index) => index === selectedRecordIndex ? updated : item));
      }
      return updated;
    });
  };
  const loadFile = async (files) => {
    if (!analysisProfile) {
      setError("Selecciona si la persona es deportista o corresponde a salud general antes de cargar el PDF.");
      return;
    }
    setBusy(true);
    setError("");
    setProgress({ pageNumber: 0, pageCount: 0 });
    try {
      const selectedFiles = analysisMode === "comparison" ? files : files.slice(0, 1);
      const parsedRecords = [];
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const extracted = await extractDexaPdf(selectedFiles[index], {
          onProgress: (value) => setProgress({ ...value, fileNumber: index + 1, fileCount: selectedFiles.length }),
        });
        const parsed = parseDexaPages(extracted.pages, { fileName: extracted.fileName });
        parsed.professional = { ...parsed.professional, populationProfile: analysisProfile };
        parsedRecords.push(deriveDexaRecord(parsed));
      }
      const combinedRecords = analysisMode === "comparison"
        ? [...comparisonRecords, ...parsedRecords]
        : parsedRecords;
      const uniqueRecords = [...new Map(combinedRecords.map((item) => [comparisonRecordKey(item), item])).values()];
      const ordered = sortDexaRecords(uniqueRecords);
      if (analysisMode === "comparison" && !validateSamePatient(ordered)) {
        throw new Error("Los informes seleccionados parecen corresponder a pacientes distintos. Revisa el nombre o la identificación antes de compararlos.");
      }
      setComparisonRecords(analysisMode === "comparison" ? ordered : []);
      setSelectedRecordIndex(analysisMode === "comparison" ? ordered.length - 1 : 0);
      setRecord(ordered.at(-1));
      setActiveStep(analysisMode === "comparison" ? "import" : "review");
    } catch (fileError) {
      setError(fileError instanceof Error ? fileError.message : "No fue posible leer el PDF.");
    } finally {
      setBusy(false);
    }
  };
  const removeComparisonRecord = (indexToRemove) => {
    setComparisonRecords((items) => {
      const remaining = items.filter((_, index) => index !== indexToRemove);
      setSelectedRecordIndex(Math.max(0, remaining.length - 1));
      setRecord(remaining.at(-1) ?? null);
      return remaining;
    });
    setError("");
  };
  const continueComparison = () => {
    if (comparisonRecords.length < 2) {
      setError("Añade al menos dos informes PDF para continuar con la comparación.");
      return;
    }
    setSelectedRecordIndex(comparisonRecords.length - 1);
    setRecord(comparisonRecords.at(-1));
    setError("");
    setActiveStep("review");
  };
  const loadDemo = () => {
    if (!analysisProfile) {
      setError("Selecciona primero el perfil de análisis.");
      return;
    }
    const currentDemo = createAnonymizedDemoRecord();
    currentDemo.professional.populationProfile = analysisProfile;
    if (analysisMode === "comparison") {
      const previousDemo = structuredClone(currentDemo);
      previousDemo.exam.date = "2025-07-28";
      previousDemo.exam.sourceFileName = "ejemplo-anterior-anonimizado.pdf";
      previousDemo.composition.total.fatMassG += 1650;
      previousDemo.composition.total.leanMassG -= 900;
      previousDemo.composition.total.totalMassG += 750;
      previousDemo.composition.total.fatPercent = 22.6;
      previousDemo.composition.indices.vatAreaCm2 = 41.7;
      previousDemo.composition.indices.fmi = 4.91;
      previousDemo.composition.indices.lmi = 16.25;
      previousDemo.composition.indices.almi = 6.92;
      previousDemo.bone.wholeBody.bmd = 1.198;
      previousDemo.bone.hip.total.bmd = 0.982;
      previousDemo.bone.lumbar.total.bmd = 1.109;
      const demos = sortDexaRecords([deriveDexaRecord(previousDemo), deriveDexaRecord(currentDemo)]);
      setComparisonRecords(demos);
      setSelectedRecordIndex(1);
      setRecord(demos[1]);
    } else {
      setComparisonRecords([]);
      setSelectedRecordIndex(0);
      setRecord(deriveDexaRecord(currentDemo));
    }
    setError("");
    setActiveStep("review");
  };
  const reset = () => {
    setRecord(null);
    setComparisonRecords([]);
    setSelectedRecordIndex(0);
    setAnalysisMode("single");
    setAnalysisProfile("");
    setActiveStep("import");
    setError("");
    setProgress({ pageNumber: 0, pageCount: 0 });
    setIncludeOriginalPages(false);
  };

  return (
    <div className={`app-shell ${sidebarCollapsed ? "app-shell--sidebar-collapsed" : ""}`}>
      <AppHeader
        record={record}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        onReset={reset}
      />
      <WorkflowSidebar
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        record={record && (analysisMode !== "comparison" || comparisonRecords.length >= 2) ? record : null}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />
      <main className="workspace">
        {activeStep === "import" ? (
          <ImportStep
            record={record}
            comparisonRecords={comparisonRecords}
            analysisMode={analysisMode}
            onModeChange={(mode) => {
              setAnalysisMode(mode);
              setRecord(null);
              setComparisonRecords([]);
              setError("");
            }}
            analysisProfile={analysisProfile}
            onProfileChange={(profile) => {
              setAnalysisProfile(profile);
              setError("");
            }}
            onFile={loadFile}
            onDemo={loadDemo}
            busy={busy}
            progress={progress}
            error={error}
            onContinueComparison={continueComparison}
            onRemoveComparison={removeComparisonRecord}
          />
        ) : null}
        {record && activeStep === "review" ? (
          <>
            {analysisMode === "comparison" ? (
              <div className="comparison-record-tabs" role="tablist" aria-label="Valoraciones cargadas">
                {comparisonRecords.map((item, index) => (
                  <button key={`${item.exam.date}-${index}`} type="button" role="tab" aria-selected={selectedRecordIndex === index} className={selectedRecordIndex === index ? "is-active" : ""} onClick={() => { setSelectedRecordIndex(index); setRecord(item); }}>
                    <span>{index === comparisonRecords.length - 1 ? "Actual" : index === 0 ? "Anterior" : `Intermedia ${index}`}</span>
                    <strong>{item.exam.date || item.exam.sourceFileName}</strong>
                  </button>
                ))}
              </div>
            ) : null}
            <ReviewStep record={record} update={update} onContinue={() => setActiveStep("interpret")} />
          </>
        ) : null}
        {interpretation && activeStep === "interpret" ? (
          <>
            {comparison ? <StatusNote status="favorable" title="Comparación preparada">Se compararán {comparison.records.length} valoraciones entre {comparison.previousRecord.exam.date} y {comparison.currentRecord.exam.date}. {comparison.summary}</StatusNote> : null}
            <InterpretStep interpretation={comparison?.current || interpretation} onContinue={() => setActiveStep("report")} />
          </>
        ) : null}
        {interpretation && activeStep === "report" ? (
          <ReportStep
            interpretation={comparison?.current || interpretation}
            comparison={comparison}
            includeOriginalPages={includeOriginalPages}
            setIncludeOriginalPages={setIncludeOriginalPages}
          />
        ) : null}
      </main>
      <MobileWorkflowButton collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
    </div>
  );
}
